/**
 * IndexStaffingSection.tsx
 * Index Tests Phase 9, Item 9.4 (owner revision R2) — the People-phase
 * (Phase2Panel.tsx) "Index tests" block: one row per active IndexAllocation
 * for this scenario+source, each staffable via IndexStaffingRow.
 *
 * Mounted by Phase2Panel below the task-planning views (staff/roster/classic/
 * board all leave the panel's DOM — this section renders once, independent of
 * the view toggle, since index allocations are pooled work with no per-view
 * presentation). Renders NOTHING when the source has zero active index
 * allocations (mirrors the tab-greyed convention, I20 — no clutter for a
 * source with no index tests).
 *
 * Staffing here creates real EmployeeAssignment presence rows
 * (IndexAllocationKey set) that flow through the SAME employee-week loaders
 * as task presence (verified, not forked — see weeklyCapacityService.ts /
 * employeeBarsService.ts additive fixes landed alongside this item).
 */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Alert, Box, CircularProgress, Divider, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import ScienceIcon from '@mui/icons-material/Science';

import {
  listIndexAllocations,
  type IndexAllocation,
} from '../../../../../services/scheduleIndex';
import type { Scenario } from '../../../../../services/scheduleScenario';
import { IndexStaffingRow } from './IndexStaffingRow';

export interface IndexStaffingSectionProps {
  scenario: Scenario | null;
  /** Submitted/Archived scenario — read-only (mirrors Phase2Panel's `locked`). */
  locked: boolean;
  /** 6D.2-style on-enter refresh key (bumped when the People phase is (re-)entered). */
  onEnterRefreshKey?: number;
  /** Called after a successful assign/unassign — bubbles to Phase2Panel's
   *  notifyAllocationChanged (emits scheduleBus 'assignments-changed' + bumps
   *  the capacity refetch), so other views quietly revalidate. */
  onAllocationChanged?: () => void;
}

export const IndexStaffingSection: React.FC<IndexStaffingSectionProps> = ({
  scenario,
  locked,
  onEnterRefreshKey = 0,
  onAllocationChanged,
}) => {
  const theme = useTheme();

  const source: 'Quote' | 'WorkOrder' | null = scenario?.SourceType ?? null;
  const sourceKey: string | null =
    source === 'Quote' ? (scenario?.QuoteKey ?? null) : (scenario?.WorkOrderKey ?? null);
  const scenarioKey: string | null = scenario?.ScenarioKey ?? null;

  const [allocations, setAllocations] = useState<IndexAllocation[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadAllocations = useCallback(async () => {
    if (!scenarioKey || !source || !sourceKey) {
      setAllocations([]);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const rows = await listIndexAllocations(scenarioKey, { sourceType: source, sourceKey });
      setAllocations(rows);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load index tests.');
    } finally {
      setLoading(false);
    }
  }, [scenarioKey, source, sourceKey]);

  useEffect(() => {
    void loadAllocations();
  }, [loadAllocations]);

  // On-enter refresh (mirrors Phase2Panel / IndexTestsPanel — skip value 0).
  const onEnterRefreshKeyRef = useRef(0);
  useEffect(() => {
    if (onEnterRefreshKey === 0) return;
    if (onEnterRefreshKey === onEnterRefreshKeyRef.current) return;
    onEnterRefreshKeyRef.current = onEnterRefreshKey;
    void loadAllocations();
  }, [onEnterRefreshKey, loadAllocations]);

  const handleRowChanged = useCallback(() => {
    onAllocationChanged?.();
  }, [onAllocationChanged]);

  // No source, load error swallowed at the section level (best-effort — the
  // rest of the People phase remains usable), or zero index tests: render
  // nothing so a non-index source never shows an empty block.
  if (!scenario || !source || !sourceKey) return null;
  if (!loading && allocations.length === 0 && !error) return null;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mt: 1 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <ScienceIcon sx={{ fontSize: 18, color: theme.schedule.machineTime }} />
        <Typography variant="subtitle1" fontWeight={700} sx={{ fontSize: '0.9rem' }}>
          Index Tests
        </Typography>
        <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.7rem' }}>
          Staff people onto this source's pooled index-test allocations (planning only — never
          touches machine capacity).
        </Typography>
        {loading && <CircularProgress size={14} />}
      </Box>
      <Divider />

      {error && (
        <Alert severity="error" onClose={() => setError(null)} sx={{ fontSize: '0.8rem' }}>
          {error}
        </Alert>
      )}

      {allocations.map((allocation) => (
        <IndexStaffingRow
          key={allocation.IndexAllocationKey}
          allocation={allocation}
          locked={locked}
          onChanged={handleRowChanged}
        />
      ))}
    </Box>
  );
};
